import * as THREE from 'three';
import {
  applySurfaceMaterialDefaults,
  copySurfaceMaterialProperties,
  createSurfaceMaterial,
  surfaceProgramCacheKey,
  type SurfaceMaterialParams,
} from '../core/SurfaceMaterial';
import {
  assignDamageFieldUniforms,
  DAMAGE_FIELD_COMMON_GLSL,
  DAMAGE_FIELD_GLSL,
  DAMAGE_FIELD_VERTEX_GLSL,
  DAMAGE_FIELD_VERTEX_SAMPLE,
  damageFieldProgramParts,
  registerDamageFieldConsumer,
} from '../core/DamageFieldBinding';
import {
  applyDamageDecalAtlasDefine,
  assignDamageDecalUniforms,
  DAMAGE_DECAL_ATLAS_DEFINE,
  DAMAGE_DECAL_ATLAS_GLSL,
  DAMAGE_DECAL_VERTEX_GLSL,
  DAMAGE_DECAL_VERTEX_SAMPLE,
} from '../core/DamageDecalAtlas';
import { SURFACE_MICRO_GLSL } from '../core/SurfaceMicroDetail';
import { SURFACE_NOISE_GLSL } from '../core/SurfaceNoise';
import { SURFACE_TRIPLANAR_GLSL } from '../core/TriplanarProjection';
import { applyBakeShaderHooks } from '../core/SurfaceBakeChunks';
import type { MaterialAppearanceDescriptor } from '../appearanceDescriptor';
import {
  asFiniteNumber,
  asHexColor,
  commonSurfaceControls,
  type SurfaceControlSpec,
  type SurfaceDefinition,
  type SurfaceFace,
} from '../core/SurfaceDefinition';
import { DESTRUCTION_PROFILES } from '../../services/destructionProfiles';

export type BrickQuality = 'high' | 'medium' | 'low';
export type BrickFace = 'exterior' | 'interior';
export type BrickSurfaceState = 'hero' | 'weathered' | 'wet' | 'dusty';

export interface BrickMaterialSettings {
  /** Meters per procedural brick repeat. Courses remain aligned to rest X/Y. */
  textureScale: number;
  roughness: number;
  normalStrength: number;
  macroVariation: number;
  mortarContrast: number;
  quality: BrickQuality;
  seed: number;
  surfaceState: BrickSurfaceState;
  baseTint: string;
  roughnessMin: number;
  roughnessMax: number;
  wearAmount: number;
  /** Mortar joint half-width in brick-normalized edge units. Default 0.05. */
  mortarWidth: number;
  mortarTint: string;
  /** Extra running-bond stagger noise. Default 0 — identity. */
  courseJitter: number;
}

export const DEFAULT_BRICK_SETTINGS: BrickMaterialSettings = {
  textureScale: 0.46,
  roughness: 0.84,
  normalStrength: 0.2,
  macroVariation: 0.28,
  mortarContrast: 0.82,
  quality: 'medium',
  seed: 3187,
  surfaceState: 'hero',
  baseTint: '#ffffff',
  roughnessMin: 0.34,
  roughnessMax: 0.98,
  wearAmount: 0,
  mortarWidth: 0.05,
  mortarTint: '#ffffff',
  courseJitter: 0,
};

/** Red-clay chips + separate grey mortar dust (two populations). */
export const appearanceDescriptor = {
  version: 1,
  dust: { color: '#9a968c', secondaryColor: '#c44e32', lingerScale: 0.92, settleSpeed: 0.26 },
  chips: { color: '#c44e32' },
  debrisTint: '#b04a30',
  particleStyle: {},
  fragment: { outer: '#9a4a35', interior: '#8a8680' },
} as const satisfies MaterialAppearanceDescriptor;

interface BrickUniforms {
  uBrickTextureScale: { value: number };
  uBrickRoughness: { value: number };
  uBrickNormalStrength: { value: number };
  uBrickMacroVariation: { value: number };
  uBrickMortarContrast: { value: number };
  uBrickQuality: { value: number };
  uBrickSeed: { value: number };
  uBrickInteriorMode: { value: number };
  uBrickSurfaceState: { value: number };
  uBrickBaseTint: { value: THREE.Color };
  uBrickRoughnessMin: { value: number };
  uBrickRoughnessMax: { value: number };
  uBrickWearAmount: { value: number };
  uBrickMortarWidth: { value: number };
  uBrickMortarTint: { value: THREE.Color };
  uBrickCourseJitter: { value: number };
}

interface BrickMaterialState {
  face: BrickFace;
  settings: BrickMaterialSettings;
  uniforms: BrickUniforms;
}

const brickStates = new WeakMap<THREE.Material, BrickMaterialState>();

const BRICK_GLSL = /* glsl */ `
uniform float uBrickTextureScale;
uniform float uBrickRoughness;
uniform float uBrickNormalStrength;
uniform float uBrickMacroVariation;
uniform float uBrickMortarContrast;
uniform float uBrickQuality;
uniform float uBrickSeed;
uniform float uBrickInteriorMode;
uniform float uBrickSurfaceState;
uniform vec3 uBrickBaseTint;
uniform float uBrickRoughnessMin;
uniform float uBrickRoughnessMax;
uniform float uBrickWearAmount;
uniform float uBrickMortarWidth;
uniform vec3 uBrickMortarTint;
uniform float uBrickCourseJitter;

// Grid identity for damage decals (filled by brickSurfaceFields).
float gBrickEdgeX = 0.5;
float gBrickEdgeY = 0.5;
float gBrickRow = 0.0;
float gBrickCol = 0.0;
float gBrickFaceX = 0.0;
float gBrickFaceY = 0.0;

float brickFbm(vec3 p, float wavelengthMeters, float fp) {
  float v = surfaceNoiseBL(p, uBrickSeed * 13.17, wavelengthMeters, fp);
  if (uBrickQuality > 0.5) v += surfaceNoiseBL(p * 2.03 + vec3(17,43,29), uBrickSeed * 13.17, wavelengthMeters / 2.03, fp) * 0.45;
  if (uBrickQuality > 1.5) v += surfaceNoiseBL(p * 4.01 + vec3(31,11,47), uBrickSeed * 13.17, wavelengthMeters / 4.01, fp) * 0.2;
  return v / (uBrickQuality > 1.5 ? 1.65 : (uBrickQuality > 0.5 ? 1.45 : 1.0));
}

void brickSurfaceFields(vec3 restPos, vec3 restNormal, out float broad, out float mortar, out float chip, out float height) {
  vec3 n = normalize(restNormal);
  vec3 w = surfaceTriplanarWeights(n);
  // Material frame: X is the running-bond axis and Y is the course axis.
  vec2 front = restPos.xy;
  vec2 side = restPos.zy;
  vec2 top = restPos.xz;
  float scale = max(uBrickTextureScale, 0.05);
  float fp = surfacePixelFootprint(restPos);
  vec2 uv = (front * w.z + side * w.x + top * w.y) / scale;
  float course = uv.y / 0.46;
  float row = floor(course);
  float stagger = mod(row, 2.0) * 0.5;
  float rowJitter = (surfaceHash_(vec3(row, 7.0, 13.0), uBrickSeed * 13.17) - 0.5) * uBrickCourseJitter;
  float col = floor(uv.x / 0.98 + stagger + rowJitter);
  float brickX = fract(uv.x / 0.98 + stagger + rowJitter);
  float brickY = fract(course);
  float edgeX = min(brickX, 1.0 - brickX);
  float edgeY = min(brickY, 1.0 - brickY);
  gBrickEdgeX = edgeX;
  gBrickEdgeY = edgeY;
  gBrickRow = row;
  gBrickCol = col;
  gBrickFaceX = brickX;
  gBrickFaceY = brickY;
  // edgeX/Y are brick-normalized (0–0.5). X spans 0.98*scale m; Y spans 0.46*scale m.
  // Convert footprint on the thinner course axis so neither joint is < ~1.5 px.
  float metersPerEdge = 0.46 * scale;
  float fpInEdgeUnits = fp / max(metersPerEdge, 1e-6);
  // Widen SYMMETRICALLY around the original 50% threshold (0.05) so the joint
  // keeps its visual width and only the transition softens — anchoring the
  // near edge and pushing the far edge out fattened joints into pale slabs.
  // pow(k<1) compresses the 10–90% onset; 1.0 keeps that span ≥ ~1.5 px.
  float mortarHalfW = max(0.025, fpInEdgeUnits * 1.0);
  mortar = 1.0 - smoothstep(uBrickMortarWidth - mortarHalfW, uBrickMortarWidth + mortarHalfW, min(edgeX, edgeY));
  mortar = pow(clamp(mortar, 0.0, 1.0), mix(1.6, 0.65, uBrickMortarContrast));
  broad = brickFbm(restPos / scale * 0.72 + vec3(5,13,7), scale / 0.72, fp);
  float chipFade = surfaceOctaveFade(scale / 7.0, fp);
  float chipRaw = uBrickQuality > 0.5 ? smoothstep(0.76, 0.96, brickFbm(restPos / scale * 7.0 + vec3(19,3,31), scale / 7.0, fp)) : 0.0;
  chip = chipRaw * chipFade;
  // Original mortar outer half-width 0.075 edge units × 2 sides × thinner axis.
  float grooveWidthMeters = 3.0 * uBrickMortarWidth * metersPerEdge;
  float grooveFade = surfaceOctaveFade(grooveWidthMeters, fp);
  height = 0.5 + (broad - 0.5) * 0.07 - mortar * 0.095 * grooveFade - chipRaw * 0.025 * chipFade;
  if (uBrickInteriorMode > 0.5) {
    // Raw clay body: no courses, no mortar grooves.
    mortar = 0.0;
    chip = 0.0;
    height = 0.5 + (broad - 0.5) * 0.18;
  }
}
`;

const BRICK_VERTEX_INJECTION = /* glsl */ `
attribute vec3 surfaceRestPosition;
varying vec3 vBrickRestPosition;
varying vec3 vBrickRestNormal;
${DAMAGE_FIELD_COMMON_GLSL}
${DAMAGE_FIELD_VERTEX_GLSL}
${DAMAGE_DECAL_VERTEX_GLSL}
`;

const BRICK_FRAGMENT_INJECTION = /* glsl */ `
uniform mat3 normalMatrix;
varying vec3 vBrickRestPosition;
varying vec3 vBrickRestNormal;
${SURFACE_TRIPLANAR_GLSL}
${SURFACE_NOISE_GLSL}
${SURFACE_MICRO_GLSL}
${DAMAGE_FIELD_GLSL}
${DAMAGE_DECAL_ATLAS_GLSL}
${BRICK_GLSL}
float gBrickBroad;
float gBrickMortar;
float gBrickChip;
float gBrickHeight;
`;

function clamp01(value: number, fallback = 0): number {
  return Math.min(Math.max(Number.isFinite(value) ? value : fallback, 0), 1);
}
function qualityValue(value: BrickQuality): number { return value === 'high' ? 2 : value === 'medium' ? 1 : 0; }
function stateValue(value: BrickSurfaceState): number {
  return value === 'weathered' ? 1 : value === 'wet' ? 2 : value === 'dusty' ? 3 : 0;
}
function normalizeSettings(settings: BrickMaterialSettings): BrickMaterialSettings {
  return {
    textureScale: Number.isFinite(settings.textureScale) && settings.textureScale > 0 ? settings.textureScale : DEFAULT_BRICK_SETTINGS.textureScale,
    roughness: clamp01(settings.roughness, DEFAULT_BRICK_SETTINGS.roughness),
    normalStrength: Math.min(Math.max(Number.isFinite(settings.normalStrength) ? settings.normalStrength : 0, 0), 0.8),
    macroVariation: clamp01(settings.macroVariation, DEFAULT_BRICK_SETTINGS.macroVariation),
    mortarContrast: clamp01(settings.mortarContrast, DEFAULT_BRICK_SETTINGS.mortarContrast),
    quality: settings.quality === 'high' || settings.quality === 'low' ? settings.quality : 'medium',
    seed: Number.isFinite(settings.seed) ? settings.seed : DEFAULT_BRICK_SETTINGS.seed,
    surfaceState: settings.surfaceState === 'weathered' || settings.surfaceState === 'wet' || settings.surfaceState === 'dusty' ? settings.surfaceState : 'hero',
    baseTint: asHexColor(settings.baseTint, DEFAULT_BRICK_SETTINGS.baseTint),
    roughnessMin: clamp01(asFiniteNumber(settings.roughnessMin, DEFAULT_BRICK_SETTINGS.roughnessMin), DEFAULT_BRICK_SETTINGS.roughnessMin),
    roughnessMax: clamp01(asFiniteNumber(settings.roughnessMax, DEFAULT_BRICK_SETTINGS.roughnessMax), DEFAULT_BRICK_SETTINGS.roughnessMax),
    wearAmount: clamp01(asFiniteNumber(settings.wearAmount, DEFAULT_BRICK_SETTINGS.wearAmount), DEFAULT_BRICK_SETTINGS.wearAmount),
    mortarWidth: Math.min(Math.max(asFiniteNumber(settings.mortarWidth, DEFAULT_BRICK_SETTINGS.mortarWidth), 0.01), 0.2),
    mortarTint: asHexColor(settings.mortarTint, DEFAULT_BRICK_SETTINGS.mortarTint),
    courseJitter: clamp01(asFiniteNumber(settings.courseJitter, DEFAULT_BRICK_SETTINGS.courseJitter), DEFAULT_BRICK_SETTINGS.courseJitter),
  };
}
function createUniforms(settings: BrickMaterialSettings, face: BrickFace): BrickUniforms {
  return {
    uBrickTextureScale: { value: settings.textureScale }, uBrickRoughness: { value: settings.roughness },
    uBrickNormalStrength: { value: settings.normalStrength }, uBrickMacroVariation: { value: settings.macroVariation },
    uBrickMortarContrast: { value: settings.mortarContrast }, uBrickQuality: { value: qualityValue(settings.quality) },
    uBrickSeed: { value: settings.seed }, uBrickInteriorMode: { value: face === 'interior' ? 1 : 0 },
    uBrickSurfaceState: { value: stateValue(settings.surfaceState) },
    uBrickBaseTint: { value: new THREE.Color(settings.baseTint) },
    uBrickRoughnessMin: { value: Math.min(settings.roughnessMin, settings.roughnessMax) },
    uBrickRoughnessMax: { value: Math.max(settings.roughnessMin, settings.roughnessMax) },
    uBrickWearAmount: { value: settings.wearAmount },
    uBrickMortarWidth: { value: settings.mortarWidth },
    uBrickMortarTint: { value: new THREE.Color(settings.mortarTint) },
    uBrickCourseJitter: { value: settings.courseJitter },
  };
}
function syncUniforms(state: BrickMaterialState): void {
  const { settings, uniforms, face } = state;
  uniforms.uBrickTextureScale.value = settings.textureScale; uniforms.uBrickRoughness.value = settings.roughness;
  uniforms.uBrickNormalStrength.value = settings.normalStrength; uniforms.uBrickMacroVariation.value = settings.macroVariation;
  uniforms.uBrickMortarContrast.value = settings.mortarContrast; uniforms.uBrickQuality.value = qualityValue(settings.quality);
  uniforms.uBrickSeed.value = settings.seed; uniforms.uBrickInteriorMode.value = face === 'interior' ? 1 : 0;
  uniforms.uBrickSurfaceState.value = stateValue(settings.surfaceState);
  uniforms.uBrickBaseTint.value.set(settings.baseTint);
  uniforms.uBrickRoughnessMin.value = Math.min(settings.roughnessMin, settings.roughnessMax);
  uniforms.uBrickRoughnessMax.value = Math.max(settings.roughnessMin, settings.roughnessMax);
  uniforms.uBrickWearAmount.value = settings.wearAmount;
  uniforms.uBrickMortarWidth.value = settings.mortarWidth;
  uniforms.uBrickMortarTint.value.set(settings.mortarTint);
  uniforms.uBrickCourseJitter.value = settings.courseJitter;
}

function compileBrickShader(shader: { uniforms: Record<string, { value: unknown }>; vertexShader: string; fragmentShader: string }, state: BrickMaterialState): void {
  Object.assign(shader.uniforms, state.uniforms);
  assignDamageFieldUniforms(shader);
  assignDamageDecalUniforms(shader);
  shader.vertexShader = shader.vertexShader
    .replace('#include <common>', `${BRICK_VERTEX_INJECTION}\n#include <common>`)
    .replace(
      '#include <worldpos_vertex>',
      `#include <worldpos_vertex>
vBrickRestPosition = surfaceRestPosition;
vBrickRestNormal = normalize(normal);
${DAMAGE_FIELD_VERTEX_SAMPLE}
${DAMAGE_DECAL_VERTEX_SAMPLE}`,
    );
  shader.fragmentShader = shader.fragmentShader
    .replace('#include <common>', `${BRICK_FRAGMENT_INJECTION}\n#include <common>`)
    .replace('#include <color_fragment>', `#include <color_fragment>
gSurfaceSpecAaRough = 0.0;
brickSurfaceFields(vBrickRestPosition, vBrickRestNormal, gBrickBroad, gBrickMortar, gBrickChip, gBrickHeight);
float brickMicro = surfaceMicroField(vBrickRestPosition, max(uBrickTextureScale * 0.095, 0.012), uBrickSeed + 31.0);
float brickLodFade = surfaceMicroFade(vBrickRestPosition, max(uBrickTextureScale * 0.095, 0.012));
vec3 brickBody = mix(vec3(0.38, 0.12, 0.075), vec3(0.64, 0.27, 0.17), gBrickBroad);
brickBody *= 1.0 + (gBrickBroad - 0.5) * uBrickMacroVariation * 0.3;
brickBody *= 0.97 + brickMicro * 0.06;
vec3 mortarTint = mix(vec3(0.32, 0.31, 0.28), vec3(0.58, 0.56, 0.51), gBrickBroad) * uBrickMortarTint;
vec3 brickAlbedo = mix(brickBody, mortarTint, gBrickMortar * uBrickMortarContrast);
// F3: near-impact freckles suppressed; corner chips applied after damage sample.
brickAlbedo = mix(brickAlbedo, vec3(0.26, 0.09, 0.055), gBrickChip * 0.22);
if (uBrickInteriorMode > 0.5) {
  // Fresh clay body, no courses. Tonal anchor: appearance.fragment.interior #8a8680.
  // Sharp clay tear mottle — albedo contrast, not just roughness.
  vec3 brickClayBody = vec3(0.541, 0.525, 0.502);
  float brickClayFp = surfacePixelFootprint(vBrickRestPosition);
  float brickClayM = max(uBrickTextureScale, 0.05);
  float brickClayGrain = surfaceNoiseBL(vBrickRestPosition / brickClayM * 5.5 + vec3(3.0, 8.0, 1.0), uBrickSeed * 21.7, brickClayM / 5.5, brickClayFp);
  float brickClayFine = surfaceNoiseBL(vBrickRestPosition / brickClayM * 12.0 + vec3(9.0, 2.0, 5.0), uBrickSeed * 21.7, brickClayM / 12.0, brickClayFp);
  float brickTear = surfaceNoiseBL(vBrickRestPosition / brickClayM * 3.2 + vec3(4.0, 15.0, 7.0), uBrickSeed * 21.7, brickClayM / 3.2, brickClayFp);
  float brickTear2 = surfaceNoiseBL(vBrickRestPosition / brickClayM * 9.0 + vec3(12.0, 3.0, 8.0), uBrickSeed * 21.7, brickClayM / 9.0, brickClayFp);
  float tearAmt = smoothstep(0.46, 0.78, brickTear) * surfaceOctaveFade(brickClayM / 3.2, brickClayFp);
  float tearFine = smoothstep(0.60, 0.88, brickTear2) * surfaceOctaveFade(brickClayM / 9.0, brickClayFp);
  brickAlbedo = mix(brickClayBody * 0.48, brickClayBody * 1.28, mix(brickClayGrain, brickClayFine, 0.35));
  brickAlbedo = mix(brickAlbedo, brickAlbedo * vec3(0.32, 0.24, 0.20), tearAmt * 0.82);
  brickAlbedo = mix(brickAlbedo, brickAlbedo * vec3(0.48, 0.36, 0.30), tearFine * 0.48);
  float brickFiss = surfaceNoiseBL(vBrickRestPosition / brickClayM * 16.0 + vec3(7.0, 1.0, 13.0), uBrickSeed * 21.7, brickClayM / 16.0, brickClayFp);
  float fissAmt = smoothstep(0.68, 0.93, brickFiss) * surfaceOctaveFade(brickClayM / 16.0, brickClayFp);
  brickAlbedo *= mix(1.0, 0.42, fissAmt);
  brickAlbedo *= 0.97 + brickMicro * 0.08;
}
if (uBrickSurfaceState > 0.5 && uBrickSurfaceState < 1.5) brickAlbedo *= mix(1.0, 0.72, gBrickBroad * 0.5);
else if (uBrickSurfaceState > 1.5 && uBrickSurfaceState < 2.5) brickAlbedo *= 0.74 + 0.26 * gBrickMortar;
else if (uBrickSurfaceState > 2.5) brickAlbedo = mix(brickAlbedo, mortarTint * 1.15, gBrickMortar * 0.3);
#ifdef USE_DAMAGE_FIELD
fillDamageGlobals(vBrickRestPosition, normalize(vBrickRestNormal));
float brickFresh = 1.0 - gDamageAge * 0.32;
// Interior clay: brighter, more saturated orange-red than weathered face.
vec3 brickInteriorClay = vec3(0.82, 0.34, 0.16);
#ifdef USE_DAMAGE_DECAL_ATLAS
sampleBrickDamageDecals(
  vBrickRestPosition,
  normalize(vBrickRestNormal),
  uBrickTextureScale,
  gBrickMortar,
  gBrickChip,
  gBrickEdgeX,
  gBrickEdgeY,
  gBrickRow,
  gBrickCol,
  gBrickFaceX,
  gBrickFaceY
);
// Depth recess on spalled faces + lit rim height lip (D8 recipe).
gBrickHeight = clamp(gBrickHeight - gDecalDepth * 0.14 - gBrickStairCrack * 0.04 + gDecalRim * 0.05 - gBrickCornerChip * 0.03, 0.0, 1.0);
// D7 lesson: field bruise is a whisper through chew-modulated blotch only.
// No gDamageCore box, no field-driven darkening, no lattice crack.
brickAlbedo = mix(brickAlbedo, brickAlbedo * vec3(0.90, 0.86, 0.82), gDamageBlotch * 0.04 * brickFresh);

// F2: per-brick spall → interior clay with coarser grain (band-limited).
float brickFp = surfacePixelFootprint(vBrickRestPosition);
float brickMeters = max(uBrickTextureScale, 0.05);
vec3 brickClayP = vBrickRestPosition * (1.0 / brickMeters);
float gBrickClayGrain = surfaceNoiseBL(brickClayP * 6.0 + vec3(3.0, 8.0, 1.0), uBrickSeed * 21.7, brickMeters / 6.0, brickFp) * 0.55
  + surfaceNoiseBL(brickClayP * 12.0 + vec3(9.0, 2.0, 5.0), uBrickSeed * 21.7, brickMeters / 12.0, brickFp) * 0.30
  + brickMicro * 0.15;
// Brighter saturated clay; depth grades recess without turning to soot.
vec3 brickSpallMean = mix(
  brickInteriorClay * 1.05,
  brickInteriorClay * 0.78,
  clamp(gDecalDepth, 0.0, 1.0)
);
brickSpallMean *= mix(0.90, 1.18, gBrickClayGrain);
// Hairline shrinkage cracks inside exposed clay (atlas R). Low contrast so
// the patch border does not read as a dotted perforation (B3).
brickSpallMean = mix(brickSpallMean, brickSpallMean * vec3(0.84, 0.68, 0.52), gDecalCrack * 0.22);
float brickSpallAmt = gDecalSpall * mix(0.88, 1.0, clamp(gDecalDepth, 0.0, 1.0)) * (1.0 - gBrickSkid * 0.65);
brickAlbedo = mix(brickAlbedo, brickSpallMean, brickSpallAmt);
// F4: grazing skid lightens/abrades faces + smears mortar (not darkens).
vec3 brickSkidTint = mix(brickAlbedo * vec3(1.18, 1.12, 1.05), mortarTint * 1.22, gBrickMortar * 0.4);
brickAlbedo = mix(brickAlbedo, brickSkidTint, gBrickSkid * 0.82);
// Lit rim at spall boundary (chipped clay edge catch-light).
vec3 brickRimTint = mix(brickInteriorClay * 1.28, brickAlbedo * vec3(1.24, 1.16, 1.08), 0.3);
brickAlbedo = mix(brickAlbedo, brickRimTint, gDecalRim * 0.95 * gDecalFresh);
// F3: corner chips tinted interior clay.
brickAlbedo = mix(brickAlbedo, brickInteriorClay * 0.92, gBrickCornerChip * 0.82);
// F1 LAST: previous order mixed stair then F2/rim/F3, which washed the
// mortar AA fringe. Multiplicative 40% drop + dark hairline inside the joint.
vec3 stairMortarDark = vec3(0.032, 0.026, 0.020);
brickAlbedo *= mix(vec3(1.0), vec3(0.55, 0.53, 0.51), clamp(gBrickStairCrack, 0.0, 1.0));
brickAlbedo = mix(brickAlbedo, stairMortarDark, clamp(gBrickStairHair, 0.0, 1.0));
#else
// Interior cut faces: field bruise + lattice (atlas is exterior-only).
vec3 brickClay = vec3(0.62, 0.38, 0.27);
brickAlbedo = mix(brickAlbedo, brickClay, gDamageBlotch * mix(0.28, 0.58, clamp(gDamage, 0.0, 1.0)) * brickFresh);
brickAlbedo = mix(brickAlbedo, brickAlbedo * vec3(0.16, 0.08, 0.05), gDamageCore * 0.82 * brickFresh);
brickAlbedo = mix(brickAlbedo, brickAlbedo * vec3(0.14, 0.07, 0.05), gDamageCrack * brickFresh);
#endif
#endif
brickAlbedo *= uBrickBaseTint;
brickAlbedo = mix(brickAlbedo, brickAlbedo * vec3(0.82, 0.78, 0.72), clamp(uBrickWearAmount, 0.0, 1.0));
diffuseColor.rgb = brickAlbedo;`)
    .replace('#include <roughnessmap_fragment>', `#include <roughnessmap_fragment>
roughnessFactor = clamp(uBrickRoughness + gBrickMortar * 0.1 + (gBrickBroad - 0.5) * 0.1 + gBrickChip * 0.07 + (brickMicro - 0.5) * 0.08, 0.34, 0.98);
roughnessFactor = clamp(roughnessFactor + (1.0 - brickLodFade) * 0.05, 0.34, 0.98);
float brickGrooveFade = surfaceOctaveFade(3.0 * uBrickMortarWidth * 0.46 * max(uBrickTextureScale, 0.05), surfacePixelFootprint(vBrickRestPosition));
roughnessFactor = clamp(roughnessFactor + gBrickMortar * (1.0 - brickGrooveFade) * 0.05, 0.34, 0.98);
// Sink Toksvig slot (filled after surfaceHeightRestNormal in normal_fragment_maps; reapplied there).
roughnessFactor = clamp(roughnessFactor + gSurfaceSpecAaRough, 0.34, 0.98);
if (uBrickSurfaceState > 1.5 && uBrickSurfaceState < 2.5) roughnessFactor = clamp(roughnessFactor - gBrickBroad * 0.22, 0.18, 0.98);
#ifdef USE_DAMAGE_FIELD
#ifdef USE_DAMAGE_DECAL_ATLAS
roughnessFactor = clamp(
  roughnessFactor
    + gDamageBlotch * 0.03
    + gDecalSpall * (0.10 + gBrickClayGrain * 0.06)
    + gDecalDepth * 0.08
    + gBrickStairCrack * 0.10
    + gBrickStairHair * 0.06
    + gBrickSkid * 0.05
    + gBrickCornerChip * 0.04,
  0.18,
  0.98
);
#else
roughnessFactor = clamp(roughnessFactor + gDamageBlotch * 0.07 + gDamageCrack * 0.08, 0.18, 0.98);
#endif
#endif`)
    .replace('#include <normal_fragment_maps>', `#include <normal_fragment_maps>
vec3 brickBaseN = normalize(vBrickRestNormal);
vec3 brickRelief = surfaceHeightRestNormal(brickBaseN, vBrickRestPosition, gBrickHeight, uBrickNormalStrength * 0.72);
vec3 brickMicroN = surfaceMicroNormal(brickBaseN, vBrickRestPosition, max(uBrickTextureScale * 0.095, 0.012), uBrickNormalStrength * 0.2, uBrickSeed + 31.0);
normal = normalize(normal + normalMatrix * ((brickRelief - brickBaseN) + (brickMicroN - brickBaseN)));
// Apply sink Toksvig after height normals fill gSurfaceSpecAaRough (three.js runs this after roughnessmap_fragment).
roughnessFactor = clamp(roughnessFactor + gSurfaceSpecAaRough, 0.18, 0.98);
if (uBrickInteriorMode > 0.5) {
  roughnessFactor = clamp(roughnessFactor + 0.06, 0.40, 0.98);
}
float brickRSpan = max(0.64, 1e-4);
float brickRT = clamp((roughnessFactor - 0.34) / brickRSpan, 0.0, 1.0);
roughnessFactor = mix(uBrickRoughnessMin, max(uBrickRoughnessMin, uBrickRoughnessMax), brickRT);`)
    .replace('#include <aomap_fragment>', `#include <aomap_fragment>
reflectedLight.indirectDiffuse *= 1.0 - gBrickMortar * 0.08 - gBrickChip * 0.1;
#ifdef USE_DAMAGE_FIELD
#ifdef USE_DAMAGE_DECAL_ATLAS
reflectedLight.indirectDiffuse *= 1.0 - gDecalAo * 0.42 - gBrickStairCrack * 0.28 - gBrickStairHair * 0.18 - gDecalDepth * 0.10;
#endif
#endif`);
}

function applyBrickDefaults(material: THREE.MeshStandardMaterial, settings: BrickMaterialSettings): void {
  applySurfaceMaterialDefaults(material, { roughness: settings.roughness, metalness: 0, shaderOwnsAlbedo: true });
}

export function createBrickMaterial(params: SurfaceMaterialParams, settings: BrickMaterialSettings, face: BrickFace): THREE.MeshStandardMaterial {
  const normalized = normalizeSettings(settings);
  const material = createSurfaceMaterial(params, { roughness: normalized.roughness, metalness: 0, shaderOwnsAlbedo: true });
  const state: BrickMaterialState = { face, settings: normalized, uniforms: createUniforms(normalized, face) };
  applyBrickDefaults(material, normalized);
  material.onBeforeCompile = (shader) => {
    compileBrickShader(shader, state);
    applyBakeShaderHooks(shader, material);
  };
  // Atlas decals are exterior-only: cut faces must not paint authored spall across fractures.
  const decalEnabled = face === 'exterior';
  if (decalEnabled) applyDamageDecalAtlasDefine(material);
  material.customProgramCacheKey = () =>
    surfaceProgramCacheKey(
      'brick',
      4,
      state.face,
      state.settings.quality,
      decalEnabled ? DAMAGE_DECAL_ATLAS_DEFINE : 'no-decal',
      ...damageFieldProgramParts(),
    );
  brickStates.set(material, state);
  registerDamageFieldConsumer(material);
  return material;
}
export function isBrickMaterial(material: THREE.Material): material is THREE.MeshStandardMaterial {
  return material instanceof THREE.MeshStandardMaterial && brickStates.has(material);
}
export function updateBrickMaterial(material: THREE.Material, settings: BrickMaterialSettings): void {
  const state = brickStates.get(material);
  if (!state) throw new TypeError('updateBrickMaterial expects a material created by createBrickMaterial');
  state.settings = normalizeSettings(settings); syncUniforms(state);
  if (material instanceof THREE.MeshStandardMaterial) { applyBrickDefaults(material, state.settings); material.needsUpdate = true; }
}
export function cloneBrickMaterial(source: THREE.Material): THREE.MeshStandardMaterial {
  const state = brickStates.get(source);
  if (!state || !(source instanceof THREE.MeshStandardMaterial)) throw new TypeError('cloneBrickMaterial expects a material created by createBrickMaterial');
  const clone = createBrickMaterial({ color: source.color.clone(), roughness: source.roughness, metalness: source.metalness, flatShading: source.flatShading, side: source.side, transparent: source.transparent, opacity: source.opacity, depthWrite: source.depthWrite, alphaTest: source.alphaTest, vertexColors: source.vertexColors, name: source.name }, state.settings, state.face);
  copySurfaceMaterialProperties(source, clone);
  return clone;
}

const BRICK_CONTROLS: SurfaceControlSpec[] = [
  ...commonSurfaceControls(DEFAULT_BRICK_SETTINGS),
  {
    key: 'mortarWidth',
    label: 'Mortar width',
    kind: 'range',
    min: 0.02,
    max: 0.14,
    step: 0.005,
    default: DEFAULT_BRICK_SETTINGS.mortarWidth,
    group: 'detail',
  },
  { key: 'mortarTint', label: 'Mortar tint', kind: 'color', default: DEFAULT_BRICK_SETTINGS.mortarTint, group: 'detail' },
  {
    key: 'courseJitter',
    label: 'Course jitter',
    kind: 'range',
    min: 0,
    max: 0.35,
    step: 0.01,
    default: DEFAULT_BRICK_SETTINGS.courseJitter,
    group: 'detail',
  },
  {
    key: 'mortarContrast',
    label: 'Mortar contrast',
    kind: 'range',
    min: 0,
    max: 1,
    step: 0.01,
    default: DEFAULT_BRICK_SETTINGS.mortarContrast,
    group: 'detail',
  },
];

export const SURFACE_DEFINITION: SurfaceDefinition<BrickMaterialSettings> = {
  id: 'brick',
  label: 'Brick',
  appearance: appearanceDescriptor,
  physics: DESTRUCTION_PROFILES.brick,
  defaultSettings: DEFAULT_BRICK_SETTINGS,
  controls: BRICK_CONTROLS,
  settingsFromControls(values) {
    return {
      ...DEFAULT_BRICK_SETTINGS,
      seed: asFiniteNumber(values.seed, DEFAULT_BRICK_SETTINGS.seed),
      textureScale: asFiniteNumber(values.textureScale, DEFAULT_BRICK_SETTINGS.textureScale),
      baseTint: asHexColor(values.baseTint, DEFAULT_BRICK_SETTINGS.baseTint),
      roughnessMin: asFiniteNumber(values.roughnessMin, DEFAULT_BRICK_SETTINGS.roughnessMin),
      roughnessMax: asFiniteNumber(values.roughnessMax, DEFAULT_BRICK_SETTINGS.roughnessMax),
      normalStrength: asFiniteNumber(values.normalStrength, DEFAULT_BRICK_SETTINGS.normalStrength),
      wearAmount: asFiniteNumber(values.wearAmount, DEFAULT_BRICK_SETTINGS.wearAmount),
      mortarWidth: asFiniteNumber(values.mortarWidth, DEFAULT_BRICK_SETTINGS.mortarWidth),
      mortarTint: asHexColor(values.mortarTint, DEFAULT_BRICK_SETTINGS.mortarTint),
      courseJitter: asFiniteNumber(values.courseJitter, DEFAULT_BRICK_SETTINGS.courseJitter),
      mortarContrast: asFiniteNumber(values.mortarContrast, DEFAULT_BRICK_SETTINGS.mortarContrast),
    };
  },
  createMaterial(params, settings, face: SurfaceFace) {
    return createBrickMaterial(params, settings, face);
  },
  updateMaterial: updateBrickMaterial,
  cloneMaterial: cloneBrickMaterial,
  isMaterial: isBrickMaterial,
};
